import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { IoCloseOutline, IoFlameOutline, IoPeopleOutline, IoSparklesOutline } from 'react-icons/io5';
import './LogMoment.css';

const DAY_INITIALS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

/**
 * The post-log moment (WS3).
 *
 * Shown after every successful report: the streak, what the reading did for
 * the community, an optional one-line reflection, and a way to undo. The
 * celebration animation is deliberately rare — first log of the day, streak
 * milestones, a finished book, or a community completion.
 */
const LogMoment = ({
  moment,
  chapterLabel,
  onClose,
  onUndo,
  onSaveReflection
}) => {
  const [reflection, setReflection] = useState('');
  const [reflectionSaved, setReflectionSaved] = useState(false);
  const [savingReflection, setSavingReflection] = useState(false);

  // Escape closes the sheet, like every other modal in the app.
  useEffect(() => {
    const handleKey = (event) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  if (!moment) return null;

  const streak = moment.streak?.current || 0;
  const days = moment.streak?.days || [];
  const firstReads = moment.contribution?.firstReads || [];
  const chaptersAdvanced = moment.contribution?.chaptersAdvanced || 0;
  const booksCompleted = moment.booksCompleted || [];
  const filledGaps = moment.filledGaps || [];

  // Celebrate sparingly: milestones only.
  const isMilestone = [7, 14, 21, 31].includes(streak);
  const shouldCelebrate = isMilestone || booksCompleted.length > 0 || firstReads.length > 0 || filledGaps.length > 0;

  const todayIndex = new Date().getDay();
  const dayLabels = Array.from({ length: 7 }, (_, i) => DAY_INITIALS[(todayIndex - 6 + i + 7) % 7]);

  const handleSaveReflection = async () => {
    const text = reflection.trim();
    if (!text || !onSaveReflection) return;

    try {
      setSavingReflection(true);
      await onSaveReflection(text);
      setReflectionSaved(true);
    } catch (error) {
      console.error('Failed to save reflection:', error);
    } finally {
      setSavingReflection(false);
    }
  };

  return (
    <div className="log-moment-backdrop" onClick={onClose}>
      <div
        className={`log-moment ${shouldCelebrate ? 'celebrate' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label="Reading logged"
        onClick={(event) => event.stopPropagation()}
      >
        <button className="log-moment-close" onClick={onClose} aria-label="Close">
          <IoCloseOutline size={22} />
        </button>

        <div className="log-moment-header">
          <span className="log-moment-check" aria-hidden="true">✓</span>
          <h2>{chapterLabel ? `${chapterLabel} logged` : 'Reading logged'}</h2>
        </div>

        {/* Streak */}
        <div className="moment-section streak-section">
          <div className="streak-headline">
            <IoFlameOutline size={20} aria-hidden="true" />
            <span>
              {streak > 0
                ? `Day ${streak} in a row`
                : 'First day of a new streak'}
            </span>
          </div>
          <div className="streak-days" aria-label={`Last 7 days: ${days.filter(Boolean).length} with reading`}>
            {days.map((read, index) => (
              <span
                key={index}
                className={`streak-dot ${read ? 'read' : ''} ${index === days.length - 1 ? 'today' : ''}`}
                title={index === days.length - 1 ? 'Today' : undefined}
              >
                <span className="streak-dot-label">{dayLabels[index]}</span>
              </span>
            ))}
          </div>
        </div>

        {/* Community contribution */}
        <div className="moment-section">
          <p className="contribution-line">
            You moved the community {chaptersAdvanced} chapter{chaptersAdvanced === 1 ? '' : 's'} closer.
          </p>
          {firstReads.length > 0 && (
            <p className="first-read-line">
              <IoSparklesOutline size={16} aria-hidden="true" />
              {firstReads.length === 1
                ? `Nobody had read ${firstReads[0].book} ${firstReads[0].chapter} yet. You did.`
                : `${firstReads.length} of those were chapters nobody had reached yet.`}
            </p>
          )}
          {filledGaps.length > 0 && (
            <p className="filled-gap-line" data-testid="filled-gap-line">
              {filledGaps.length === 1 && filledGaps[0].book
                ? `You filled a gap: ${filledGaps[0].book} ${filledGaps[0].chapter}.`
                : 'You filled a gap.'}
            </p>
          )}
          {booksCompleted.length > 0 && (
            <p className="book-complete-line">
              You finished {booksCompleted.join(', ')}.
            </p>
          )}
        </div>

        {/* Church */}
        {moment.church && (
          <div className="moment-section church-section">
            <IoPeopleOutline size={16} aria-hidden="true" />
            <span>
              {moment.church.readersToday} {moment.church.readersToday === 1 ? 'person' : 'people'} from {moment.church.name} {moment.church.readersToday === 1 ? 'has' : 'have'} read today.
            </span>
          </div>
        )}

        {/* Reflection */}
        {onSaveReflection && (
          <div className="moment-section reflection-section">
            {reflectionSaved ? (
              <p className="reflection-saved">Saved.</p>
            ) : (
              <>
                <label htmlFor="moment-reflection">What stood out?</label>
                <div className="reflection-row">
                  <input
                    id="moment-reflection"
                    type="text"
                    value={reflection}
                    onChange={(event) => setReflection(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') handleSaveReflection();
                    }}
                    placeholder="Optional"
                    maxLength={1000}
                  />
                  <button
                    type="button"
                    onClick={handleSaveReflection}
                    disabled={!reflection.trim() || savingReflection}
                  >
                    {savingReflection ? 'Saving…' : 'Save'}
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        <div className="log-moment-actions">
          <button type="button" className="moment-primary" onClick={onClose}>
            Done
          </button>
          {onUndo && (
            <button type="button" className="moment-undo" onClick={onUndo}>
              Undo
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

LogMoment.propTypes = {
  moment: PropTypes.shape({
    streak: PropTypes.shape({
      current: PropTypes.number,
      days: PropTypes.arrayOf(PropTypes.bool)
    }),
    contribution: PropTypes.shape({
      chaptersAdvanced: PropTypes.number,
      firstReads: PropTypes.arrayOf(PropTypes.shape({
        book: PropTypes.string,
        chapter: PropTypes.number
      }))
    }),
    church: PropTypes.shape({
      name: PropTypes.string,
      readersToday: PropTypes.number
    }),
    booksCompleted: PropTypes.arrayOf(PropTypes.string),
    filledGaps: PropTypes.arrayOf(PropTypes.shape({
      book: PropTypes.string,
      chapter: PropTypes.number
    }))
  }),
  chapterLabel: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onUndo: PropTypes.func,
  onSaveReflection: PropTypes.func
};

export default LogMoment;
